/**
 * Invoices (F3-H): listing with filters (student/status/overdue), manual
 * charge, and generating installments from a plan over an enrollment.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { FilePlus, ListChecks, MessageCircle } from "@fleet/icons";
import { buildWaLink } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@fleet/ui";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
// AUTONOMY: the CRM's WhatsApp templates are used AT the collection point
// (they used to be orphan CRUD: created but no send offered them).
import { useTemplates } from "@/features/crm/api";
import { Money } from "./money";
import { useCreateInvoice, useGenerateInvoices, useInvoices } from "../api";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";
const MINOR = 100;

export const FinanceInvoices: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const [studentFilter, setStudentFilter] = createSignal<string | undefined>();
  const [overdueOnly, setOverdueOnly] = createSignal(false);
  const invoices = useInvoices(activeOrgId, studentFilter, () => undefined, overdueOnly);
  const createInvoice = useCreateInvoice();
  const generate = useGenerateInvoices();

  // Manual charge
  const [mStudent, setMStudent] = createSignal("");
  const [mConcept, setMConcept] = createSignal("");
  const [mAmount, setMAmount] = createSignal("");
  const [mDue, setMDue] = createSignal("");

  // Generate installments
  const [gStudent, setGStudent] = createSignal("");
  const [gEnrollment, setGEnrollment] = createSignal("");
  const [gPlan, setGPlan] = createSignal("");
  const [gDiscount, setGDiscount] = createSignal("");

  const submitManual = () => {
    const amount = Number(mAmount().replace(",", "."));
    if (!mStudent().trim() || !mConcept().trim() || !Number.isFinite(amount) || amount <= 0) {
      showToast(t(msg`Fill student, concept and amount`), "error");
      return;
    }
    createInvoice.mutate(
      {
        org: activeOrgId(),
        studentUserId: mStudent().trim(),
        concept: mConcept().trim(),
        amountUsdMinor: Math.round(amount * MINOR),
        dueDate: mDue() || undefined,
      },
      {
        onSuccess: () => {
          showToast(t(msg`Invoice created`), "success");
          setMConcept("");
          setMAmount("");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  const submitGenerate = () => {
    if (!gStudent().trim() || !gEnrollment().trim() || !gPlan().trim()) {
      showToast(t(msg`Fill student, enrollment and plan`), "error");
      return;
    }
    generate.mutate(
      {
        org: activeOrgId(),
        enrollmentId: gEnrollment().trim(),
        studentUserId: gStudent().trim(),
        planId: gPlan().trim(),
        discountCode: gDiscount().trim() || undefined,
      },
      {
        onSuccess: (res) => {
          const count = res.invoiceIds.length;
          showToast(t(msg`Generated ${count} invoice(s)`), "success");
        },
        onError: (e) => showToast(e.message, "error"),
      },
    );
  };

  // F3-I: wa.me link to collect an overdue invoice (name + USD amount).
  // AUTONOMY: if the CRM has templates, the message comes from the chosen
  // template (variables {{nombre}}/{{monto}}); otherwise from the default text.
  const templates = useTemplates(activeOrgId);
  const [templateId, setTemplateId] = createSignal("");
  const collectWaLink = (inv: {
    studentName?: string | null;
    studentPhone?: string | null;
    remainingUsdMinor: number;
  }) => {
    if (!inv.studentPhone) {
      return null;
    }
    const monto = `$${(inv.remainingUsdMinor / MINOR).toFixed(2)}`;
    const nombre = inv.studentName ?? "";
    const selected = (templates.data ?? []).find((tpl) => tpl.id === templateId());
    if (selected) {
      return buildWaLink(inv.studentPhone, selected.body, { nombre, monto });
    }
    return buildWaLink(
      inv.studentPhone,
      t(
        msg`Hi ${nombre}, you have a pending balance of ${monto} at Space Academy. How would you like to pay?`,
      ),
    );
  };

  return (
    <div class="space-y-4">
      <div class="grid gap-4 md:grid-cols-2">
        {/* Manual charge */}
        <div class="rounded-xl border border-border p-4">
          <h3 class="mb-2 flex items-center gap-2 font-semibold">
            <FilePlus class="h-4 w-4 text-primary" />
            {t(msg`Manual charge`)}
          </h3>
          <div class="space-y-2">
            <input
              class={field}
              placeholder={t(msg`Student user id`)}
              value={mStudent()}
              onInput={(e) => setMStudent(e.currentTarget.value)}
            />
            <input
              class={field}
              placeholder={t(msg`Concept`)}
              value={mConcept()}
              onInput={(e) => setMConcept(e.currentTarget.value)}
            />
            <input
              class={field}
              inputmode="decimal"
              placeholder={t(msg`Amount (USD)`)}
              value={mAmount()}
              onInput={(e) => setMAmount(e.currentTarget.value)}
            />
            <input
              class={field}
              type="date"
              value={mDue()}
              onInput={(e) => setMDue(e.currentTarget.value)}
            />
            <Button onClick={submitManual} disabled={createInvoice.isPending}>
              {t(msg`Create invoice`)}
            </Button>
          </div>
        </div>

        {/* Generate installments */}
        <div class="rounded-xl border border-border p-4">
          <h3 class="mb-2 flex items-center gap-2 font-semibold">
            <ListChecks class="h-4 w-4 text-primary" />
            {t(msg`Generate invoices from plan`)}
          </h3>
          <div class="space-y-2">
            <input
              class={field}
              placeholder={t(msg`Student user id`)}
              value={gStudent()}
              onInput={(e) => setGStudent(e.currentTarget.value)}
            />
            <input
              class={field}
              placeholder={t(msg`Enrollment id`)}
              value={gEnrollment()}
              onInput={(e) => setGEnrollment(e.currentTarget.value)}
            />
            <input
              class={field}
              placeholder={t(msg`Plan id`)}
              value={gPlan()}
              onInput={(e) => setGPlan(e.currentTarget.value)}
            />
            <input
              class={field}
              placeholder={t(msg`Discount code (optional)`)}
              value={gDiscount()}
              onInput={(e) => setGDiscount(e.currentTarget.value)}
            />
            <Button onClick={submitGenerate} disabled={generate.isPending}>
              {t(msg`Generate`)}
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      <div class="rounded-xl border border-border p-4">
        <div class="mb-2 flex flex-wrap items-center gap-2">
          <h3 class="font-semibold">{t(msg`Invoices`)}</h3>
          <input
            class={`${field} max-w-[14rem]`}
            placeholder={t(msg`Filter by student id`)}
            value={studentFilter() ?? ""}
            onInput={(e) => setStudentFilter(e.currentTarget.value.trim() || undefined)}
          />
          <label class="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={overdueOnly()}
              onChange={(e) => setOverdueOnly(e.currentTarget.checked)}
            />
            {t(msg`Overdue only`)}
          </label>
          {/* AUTONOMY: CRM's WhatsApp template for the "Collect" button. */}
          <Show when={(templates.data ?? []).length > 0}>
            <div class="ml-auto min-w-[12rem]" data-testid="collect-template-select">
              <Select
                aria-label={t(msg`WhatsApp template`)}
                value={templateId()}
                onChange={setTemplateId}
                options={[
                  { value: "", label: t(msg`Default collection message`) },
                  ...(templates.data ?? []).map((tpl) => ({ value: tpl.id, label: tpl.name })),
                ]}
              />
            </div>
          </Show>
        </div>
        <Show when={!invoices.isLoading} fallback={<Skeleton class="h-24 w-full" />}>
          <ul class="divide-y divide-border" data-testid="invoices-list">
            <For each={invoices.data}>
              {(inv) => (
                <li class="flex items-center justify-between py-2 text-sm">
                  <span class="truncate">
                    {inv.concept} {inv.seqLabel ? `· ${inv.seqLabel}` : ""}
                    <Show when={inv.overdue}>
                      <span class="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                        {t(msg`overdue`)}
                      </span>
                    </Show>
                  </span>
                  <Money usdMinor={inv.remainingUsdMinor} />
                  <span class="text-xs text-muted-foreground">{inv.dueDate ?? "—"}</span>
                  <span class="rounded-full bg-secondary/20 px-2 py-0.5 text-xs">{inv.status}</span>
                  {/* F3-I: collect the overdue invoice via WhatsApp (template with
                      name + amount). Only if the student has a phone number. */}
                  <Show when={inv.overdue && collectWaLink(inv)}>
                    {(href) => (
                      <a
                        href={href()}
                        target="_blank"
                        rel="noreferrer"
                        class="flex items-center gap-1 text-xs font-medium text-green-600 hover:underline"
                        data-testid="invoice-collect-wa"
                        aria-label={t(msg`Collect via WhatsApp`)}
                      >
                        <MessageCircle class="h-3.5 w-3.5" />
                        {t(msg`Collect`)}
                      </a>
                    )}
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </div>
  );
};
